export type ActiveSectionTask = {
  kind: "chat_async" | "agentic";
  mode: "generation" | "regeneration";
  taskId: string;
};

/** A live section record: a set `task_id` may still be running. */
export type LiveSectionRecord = {
  type?: string;
  task_id?: string | null;
  chat_async_task_id?: string | null;
  agentUpdates?: unknown[];
  content?: string;
  chat_history_id?: string | null;
};

/**
 * A section inside a downloaded workspace report: the agent task's TERMINAL
 * status is frozen on `downloadedTaskStatus`. `task_id` on this shape is
 * `never` on purpose — reading it as "still running" is the historical
 * misread these types exist to prevent (live records use `task_id` for
 * exactly that meaning).
 */
export type DownloadedSectionRecord = LiveSectionRecord & {
  fromReportDownload?: boolean;
  grounding_task_id?: string;
  downloadedTaskStatus?: "completed" | "failed" | "cancelled";
  task_id?: never;
};

export type SectionTaskRecord = LiveSectionRecord | DownloadedSectionRecord;

export const isDownloadedSectionRecord = (
  section: unknown,
): section is DownloadedSectionRecord => {
  const record = section as DownloadedSectionRecord | null | undefined;
  return Boolean(record?.fromReportDownload || record?.downloadedTaskStatus);
};

const DONE_TASK_STATUSES = ["done", "completed"];
const DONE_TASK_MESSAGES = ["task complete.", "task complete"];
const DEFAULT_TASK_FAILURE_MESSAGE = "Section generation didn't complete. Please try again.";
const FAILED_TASK_MESSAGE_PREFIX = /^failed:\s*/i;

// Rotating action copy for the empty/mid-flight agent
// state. The add-in rotates through these every 6 s while a section is
// running with no terminal update yet, so the user sees progress
// instead of the static "Waiting for an update..." string.
export const PROGRESS_PHRASES = [
  "Analyzing the source documents...",
  "Drafting the first version...",
  "Confirming citations against the source evidence...",
];

export const PROGRESS_PHRASE_INTERVAL_MS = 6000;

const getSectionType = (section: any) => String(section?.type || "").toLowerCase();

const getTaskUpdateTaskId = (update: any) => update?.task_id || update?.taskId || null;

const normalizeTaskUpdateMessage = (update: any) => String(update?.message || "").trim();

const normalizeTaskUpdateStatus = (update: any) => String(update?.status || "").toLowerCase();

const isSameTaskUpdateScope = (a: any, b: any) => {
  const aTaskId = getTaskUpdateTaskId(a);
  const bTaskId = getTaskUpdateTaskId(b);

  if (aTaskId || bTaskId) {
    return Boolean(aTaskId && bTaskId && aTaskId === bTaskId);
  }

  return true;
};

export const isDoneTaskUpdate = (update: any) => {
  const status = normalizeTaskUpdateStatus(update);
  const message = normalizeTaskUpdateMessage(update).toLowerCase();

  return DONE_TASK_STATUSES.includes(status) || DONE_TASK_MESSAGES.includes(message);
};

export const appendTaskUpdateIfNew = (updates: any[] = [], update: any) => {
  const existingUpdates = Array.isArray(updates) ? updates : [];
  const taskId = getTaskUpdateTaskId(update);
  const eventSeq = update?.event_seq;
  const message = normalizeTaskUpdateMessage(update);
  const status = normalizeTaskUpdateStatus(update);

  const alreadySeen = existingUpdates.some((existingUpdate) => {
    if (!isSameTaskUpdateScope(existingUpdate, update)) return false;

    if (eventSeq && existingUpdate?.event_seq === eventSeq) {
      return true;
    }

    return normalizeTaskUpdateMessage(existingUpdate) === message && normalizeTaskUpdateStatus(existingUpdate) === status;
  });

  if (alreadySeen) {
    return existingUpdates;
  }

  const taskAlreadyDone =
    taskId &&
    existingUpdates.some(
      (existingUpdate) => isSameTaskUpdateScope(existingUpdate, update) && isDoneTaskUpdate(existingUpdate)
    );

  if (taskAlreadyDone) {
    return existingUpdates;
  }

  return [...existingUpdates, update];
};

const inferTaskKind = (section: any): ActiveSectionTask["kind"] =>
  getSectionType(section) === "section_agentic" ? "agentic" : "chat_async";

const inferTaskMode = (section: any): ActiveSectionTask["mode"] =>
  section?.chatAsyncRegenerationInProgress || section?.agenticRegenerationInProgress ? "regeneration" : "generation";

export const createActiveSectionTask = (
  kind: ActiveSectionTask["kind"],
  mode: ActiveSectionTask["mode"],
  taskId?: string | null
): ActiveSectionTask | undefined => {
  if (!taskId) return undefined;

  return {
    kind,
    mode,
    taskId,
  };
};

export const getSectionActiveTask = (section: any): ActiveSectionTask | null => {
  const updates = Array.isArray(section?.agentUpdates) ? section.agentUpdates : [];
  const latestUpdate = updates[updates.length - 1];
  const latestStatus = normalizeTaskUpdateStatus(latestUpdate);
  const latestUpdateIsFailed = ["error", "failed", "cancelled"].includes(latestStatus);
  const isRegenerating = Boolean(section?.agenticRegenerationInProgress || section?.chatAsyncRegenerationInProgress);

  if (latestUpdateIsFailed && !isRegenerating) {
    return null;
  }

  const activeTask = section?.activeSectionTask;
  const activeTaskId = activeTask?.taskId || activeTask?.task_id;

  if (activeTaskId) {
    return {
      kind: activeTask.kind || inferTaskKind(section),
      mode: activeTask.mode || inferTaskMode(section),
      taskId: activeTaskId,
    };
  }

  const sectionType = getSectionType(section);

  if (sectionType === "section" && section?.chatAsyncInProgress && section?.chat_async_task_id) {
    return {
      kind: "chat_async",
      mode: section.chatAsyncRegenerationInProgress ? "regeneration" : "generation",
      taskId: section.chat_async_task_id,
    };
  }

  if (sectionType === "section_agentic" && section?.task_id && !section?.chat_history_id) {
    return {
      kind: "agentic",
      mode: section.agenticRegenerationInProgress ? "regeneration" : "generation",
      taskId: section.task_id,
    };
  }

  return null;
};

export const hasSectionActiveTask = (section: any) => Boolean(getSectionActiveTask(section));

export const markSectionRegenerationStarted = (section: any, kind: ActiveSectionTask["kind"]) => {
  const nextUpdate = {
    message: kind === "agentic" ? "Regenerating agent output..." : "Regenerating section...",
    status: "running",
  };

  return {
    ...section,
    activeSectionTask: undefined,
    task_id: null,
    chat_async_task_id: null,
    ...(kind === "agentic"
      ? { agenticRegenerationInProgress: true }
      : {
          chatAsyncInProgress: true,
          chatAsyncRegenerationInProgress: true,
        }),
    agentUpdates: appendTaskUpdateIfNew(section?.agentUpdates || [], nextUpdate),
  };
};

export const getSectionTaskPanelState = (section: any) => {
  const updates = Array.isArray(section?.agentUpdates) ? section.agentUpdates : [];
  const latestUpdate = updates[updates.length - 1];
  const latestStatus = normalizeTaskUpdateStatus(latestUpdate);
  const latestUpdateIsDone = isDoneTaskUpdate(latestUpdate);
  const latestUpdateIsFailed = ["error", "failed", "cancelled"].includes(latestStatus);
  const isRegenerating = Boolean(section?.agenticRegenerationInProgress || section?.chatAsyncRegenerationInProgress);
  const isActive =
    !latestUpdateIsFailed &&
    Boolean(
      getSectionActiveTask(section) || isRegenerating || section?.agenticInProgress || section?.chatAsyncInProgress
    );
  const isDone = !isActive && latestUpdateIsDone;
  const isFailed = latestUpdateIsFailed && !isRegenerating;
  const latestMessage =
    isActive && (latestUpdateIsDone || latestUpdateIsFailed)
      ? isRegenerating
        ? "Regenerating agent output..."
        : "Starting generation..."
      : normalizeTaskUpdateMessage(latestUpdate) ||
        // When a section is mid-flight and we have no concrete update yet,
        // fall back to the first rotating progress phrase instead of a
        // static "Waiting for an update..." string. The panel
        // (AgentPanel.tsx) advances through the list via setInterval; the
        // helper just guarantees the literal never reaches a user mid-run.
        (isActive ? PROGRESS_PHRASES[0] : "");
  const latestStateLabel = isActive
    ? ["file_input", "text_input", "bool_input", "select_input"].includes(latestStatus)
      ? "Input required"
      : isRegenerating
      ? "Regenerating"
      : "Generating"
    : isDone
    ? "Generation complete"
    : isFailed
    ? latestStatus === "cancelled"
      ? "Generation cancelled"
      : "Generation failed"
    : "Generation status";

  return {
    isActive,
    isRegenerating,
    isDone,
    isFailed,
    isTerminal: isDone || isFailed,
    latestUpdate,
    latestStatus,
    latestMessage,
    latestStateLabel,
    latestStateTone: isDone ? "success" : isFailed ? "error" : "running",
  };
};

/**
 * A section whose terminal state is known must NEVER fall back to the
 * empty-panel "No updates yet" placeholder — not after a reconnect, not
 * after the panel re-syncs with the document, not after the stream history
 * is restored. The panel derives terminal state from the section content
 * itself (content + chat_history_id / agent task + task_id cleared), so it
 * survives the agentUpdates array being absent or emptied on restore.
 */
export const getRestoredSectionSyncState = (section: any) => {
  const panelState = getSectionTaskPanelState(section);

  // A downloaded DOCX carries the
  // agent task's FROZEN terminal status on `downloadedTaskStatus` (never on
  // `task_id` — an active-task interpretation would make a finished
  // downloaded section look like it is still generating). This check runs
  // BEFORE the panelState early return: a downloaded section whose frozen
  // status is terminal must win over the active-task inference, which can
  // legitimately see `task_id` on hydrated sections.
  const downloaded = isDownloadedSectionRecord(section);
  const downloadedStatus = downloaded
    ? String(section.downloadedTaskStatus || "").toLowerCase()
    : "";
  const sectionType = getSectionType(section);
  const hasGeneratedContent = Boolean(String(section?.content || "").trim());

  if (downloadedStatus === "completed") {
    const updates = Array.isArray(section?.agentUpdates) ? section.agentUpdates : [];
    return {
      ...panelState,
      isDone: true,
      isTerminal: true,
      isActive: false,
      latestMessage: updates.length
        ? normalizeTaskUpdateMessage(updates[updates.length - 1]) || "Generation complete."
        : "Generation complete. Earlier process updates were not retained for this download.",
      latestStateLabel: "Generation complete",
      latestStateTone: "success" as const,
      syncStatusLabel: "Generation complete (restored)",
    };
  }
  if (downloadedStatus === "failed" || downloadedStatus === "cancelled") {
    return {
      ...panelState,
      isDone: false,
      isFailed: true,
      isTerminal: true,
      isActive: false,
      latestMessage:
        downloadedStatus === "cancelled"
          ? "Generation was cancelled before this report was downloaded."
          : "Generation failed before this report was downloaded.",
      latestStateLabel:
        downloadedStatus === "cancelled" ? "Generation cancelled" : "Generation failed",
      latestStateTone: "error" as const,
      syncStatusLabel: panelState.latestStateLabel,
    };
  }

  // Live panel state wins for in-add-in sections (active generation stays
  // active; already-terminal states pass through untouched).
  if (panelState.isTerminal || panelState.isActive) {
    return { ...panelState, syncStatusLabel: panelState.latestStateLabel };
  }

  const isNonAgenticComplete =
    sectionType === "section" && Boolean(section?.chat_history_id) && hasGeneratedContent;
  // Downloaded reports carry the task id as `grounding_task_id` (deliberately
  // NOT `task_id`, which the active-task helper interprets as still
  // running). A downloaded agentic section with its frozen grounding and
  // content is complete even when the frozen status field was absent.
  const isAgenticComplete =
    sectionType === "section_agentic" &&
    Boolean(section?.content) &&
    (Boolean(section?.task_id) ||
      Boolean(section?.fromReportDownload && section?.grounding_task_id)) &&
    Boolean(
      section?.chat_history_id ||
        section?.grounding ||
        section?.snapshotGrounding ||
        hasGeneratedContent,
    );

  if (isNonAgenticComplete || isAgenticComplete) {
    return {
      ...panelState,
      isDone: true,
      isTerminal: true,
      latestMessage: "Completed. Earlier process updates are unavailable after sync.",
      latestStateLabel: "Generation complete",
      latestStateTone: "success" as const,
      syncStatusLabel: "Generation complete (restored)",
    };
  }

  return panelState;
};

export const isRecoverableTaskInterruption = (error: any, section: any) => {
  if (error?.agenticTerminalFailure || error?.agenticReconnectExhausted) return false;
  if (!getSectionActiveTask(section)) return false;

  const message = String(error?.message || error || "").toLowerCase();
  const status = String(error?.response?.status || "");

  if (message.includes("async chat task") && message.includes("failed")) return false;
  if (status && !["0", "408", "429", "500", "502", "503", "504"].includes(status)) return false;

  return (
    (typeof navigator !== "undefined" ? !navigator.onLine : false) ||
    /network|offline|timeout|stream|sse|connection|did not complete/.test(message)
  );
};

export const formatTaskFailureMessage = (message?: string | null) => {
  const normalizedMessage = String(message || "")
    .trim()
    .replace(FAILED_TASK_MESSAGE_PREFIX, "");

  return normalizedMessage || DEFAULT_TASK_FAILURE_MESSAGE;
};

export const releaseSectionTaskOnFailure = (section: any, message: string, taskId?: string | null) => {
  const updateTaskId = taskId || section?.task_id || section?.chat_async_task_id;

  return {
    ...section,
    activeSectionTask: undefined,
    chatAsyncInProgress: false,
    chatAsyncRegenerationInProgress: false,
    agenticInProgress: false,
    agenticRegenerationInProgress: false,
    task_id: null,
    chat_async_task_id: null,
    agentUpdates: appendTaskUpdateIfNew(section?.agentUpdates || [], {
      message: formatTaskFailureMessage(message),
      status: "error",
      task_id: updateTaskId,
    }),
  };
};
